import React from 'react';

/**
 * BasicSkipToButton - A simple, accessible skip-to-main-content link
 *
 * This component provides a basic skip navigation link that:
 * - Appears at the first tab stop on every page
 * - Is visually hidden until focused
 * - Jumps to main content when activated
 * - Follows WCAG 2.0 AA accessibility guidelines
 *
 * This replaces the more complex SkipTo component with a simpler,
 * more standard approach to skip navigation.
 */
export const BasicSkipToButton: React.FC = () => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    // Find the main content element
    const mainElement =
      document.getElementById('main') ||
      document.querySelector('main') ||
      document.querySelector('[role="main"]');

    if (mainElement) {
      // Make sure the element can receive focus
      if (!mainElement.hasAttribute('tabindex')) {
        mainElement.setAttribute('tabindex', '-1');
      }

      // Focus the main content
      mainElement.focus();

      // Scroll to the element if needed
      mainElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(
        'BasicSkipToButton: No main content element found (id="main", <main>, or role="main")',
      );
    }
  };

  return (
    <a
      href="#main"
      onClick={handleClick}
      style={{
        // Visually hidden by default
        position: 'absolute',
        top: '-40px',
        left: '6px',
        zIndex: 1000,
        padding: '8px 16px',
        backgroundColor: '#000',
        color: '#fff',
        textDecoration: 'none',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: '600',
        whiteSpace: 'nowrap',
        // Become visible when focused
        transition: 'top 0.3s ease',
        outline: '2px solid transparent',
      }}
      onFocus={(e) => {
        e.currentTarget.style.top = '6px';
      }}
      onBlur={(e) => {
        e.currentTarget.style.top = '-40px';
      }}
      // Ensure it can receive focus
      tabIndex={0}
    >
      Skip to main content
    </a>
  );
};

export default BasicSkipToButton;
